/**
 * Dashboard Configuration
 * Centralized configuration for role-based dashboard tabs and content
 */

// Tab definitions
export const TABS = {
  overview: { id: 'overview', label: 'Overview' },
  projects: { id: 'projects', label: 'Projects' },
  measurements: { id: 'measurements', label: 'Measurements' },
  users: { id: 'users', label: 'Users' },
  leads: { id: 'leads', label: 'Leads' },
  distribution: { id: 'distribution', label: 'Distribution' },
  'my-leads': { id: 'my-leads', label: 'My Leads' },
  'my-projects': { id: 'my-projects', label: 'My Projects' },
  'my-measurements': { id: 'my-measurements', label: 'My Measurements' },
  'my-2d-tasks': { id: 'my-2d-tasks', label: 'My 2D Tasks' },
  'my-3d-tasks': { id: 'my-3d-tasks', label: 'My 3D Tasks' },
  'my-dd-tasks': { id: 'my-dd-tasks', label: 'My DD Tasks' },
  'my-quotation-tasks': { id: 'my-quotation-tasks', label: 'My Quotation Tasks' },
  'site-database': { id: 'site-database', label: 'Site Database' },
  'site-attendance-tracker': { id: 'site-attendance-tracker', label: 'Site Attendance' },
  'site-attendance-map': { id: 'site-attendance-map', label: 'Attendance Map' },
  'imported-data-mapping': { id: 'imported-data-mapping', label: 'Import Mapping' },
  'payment-analytics': { id: 'payment-analytics', label: 'Payment Analytics' }
};

// Role to tabs mapping
export const ROLE_TABS = {
  system_admin: ['overview', 'projects', 'measurements', 'site-database', 'site-attendance-tracker', 'site-attendance-map', 'users', 'leads', 'distribution', 'imported-data-mapping', 'payment-analytics'],
  admin: ['overview', 'projects', 'measurements', 'site-database', 'site-attendance-tracker', 'site-attendance-map', 'users', 'leads', 'distribution', 'imported-data-mapping', 'payment-analytics'],
  director: ['overview', 'projects', 'measurements', 'site-database', 'site-attendance-tracker', 'site-attendance-map', 'users', 'leads', 'distribution', 'imported-data-mapping', 'payment-analytics'],
  manager: ['overview', 'projects', 'site-database', 'site-attendance-tracker', 'site-attendance-map', 'users', 'leads', 'payment-analytics'],
  leader: ['overview', 'projects', 'measurements', 'site-database', 'users', 'leads', 'distribution'],
  agent: ['overview', 'my-leads', 'my-projects', 'measurements'],
  telemarketer: ['overview', 'my-leads', 'my-projects'],
  sid: ['overview', 'projects', 'measurements', 'site-database', 'site-attendance-tracker', 'users', 'my-2d-tasks', 'my-3d-tasks', 'my-dd-tasks'],
  asid: ['overview', 'projects', 'measurements', 'site-database', 'site-attendance-tracker', 'my-2d-tasks', 'my-3d-tasks', 'my-dd-tasks'],
  designer: ['my-3d-tasks', 'my-dd-tasks', 'overview', 'projects'],
  draughter_2d: ['my-2d-tasks', 'my-dd-tasks', 'projects'],
  quotation_team: ['my-quotation-tasks', 'overview', 'projects'],
  draughter_site: ['my-measurements'],
  legal_team: ['overview', 'projects'],
  site_supervisor: ['overview', 'site-database', 'site-attendance-tracker'],
  site_manager: ['overview', 'site-database', 'site-attendance-tracker', 'users'],
  operation_head: ['overview', 'projects', 'measurements', 'site-database', 'site-attendance-tracker', 'users', 'leads', 'distribution', 'payment-analytics']
};

// Role subtitles for the dashboard header
export const ROLE_SUBTITLES = {
  system_admin: 'System Admin Dashboard - Full System Overview',
  admin: 'Admin Dashboard - Full System Overview',
  director: 'Director Dashboard - Full System Overview',
  manager: 'Manager Dashboard - Interior Design & Renovation CRM',
  leader: 'Team Leader Dashboard - Manage Your Team',
  agent: 'Sales Agent Dashboard - Your leads and appointments at a glance',
  telemarketer: 'Telemarketer Dashboard - Lead Management',
  sid: 'SID Dashboard - Manage Your Interior Design Projects',
  asid: 'ASID Dashboard - Interior Design Projects',
  designer: 'Designer Dashboard - 3D Visualization Work',
  draughter_2d: '2D Draughter Dashboard - Technical Drawings',
  quotation_team: 'Quotation Dashboard - Project Pricing',
  draughter_site: 'Site Measurement Dashboard - View and manage your assignments',
  legal_team: 'Legal Dashboard - Contract Review',
  site_supervisor: 'Site Supervisor Dashboard - Manage your assigned sites',
  site_manager: 'Site Manager Dashboard - Oversee site operations',
  operation_head: 'Operations Dashboard - Full site management overview'
};

// Role categories for conditional logic
export const ROLE_CATEGORIES = {
  MANAGEMENT: ['system_admin', 'admin', 'director'],
  SALES: ['system_admin', 'admin', 'director', 'manager', 'leader', 'agent', 'telemarketer'],
  DESIGN_LEAD: ['sid', 'asid'],
  DESIGN_WORKER: ['designer', 'draughter_2d', 'quotation_team'],
  SITE_DRAUGHTER: ['draughter_site'],
  SITE_TEAM: ['site_supervisor', 'site_manager', 'operation_head'],
  LEGAL: ['legal_team']
};

// Stats configuration per role
export const STATS_CONFIG = {
  system_admin: [
    { key: 'totalLeads', label: 'Total Leads', color: 'tenq-gold', bgColor: 'bg-tenq-gold/10', icon: 'users' },
    { key: 'activeProjects', label: 'Active Projects', color: 'green-600', bgColor: 'bg-green-100', icon: 'document' },
    { key: 'teamMembers', label: 'Team Members', color: 'blue-600', bgColor: 'bg-blue-100', icon: 'team' },
    { key: 'conversionRate', label: 'Conversion Rate', color: 'purple-600', bgColor: 'bg-purple-100', icon: 'chart', suffix: '%' }
  ],
  admin: [
    { key: 'totalLeads', label: 'Total Leads', color: 'tenq-gold', bgColor: 'bg-tenq-gold/10', icon: 'users' },
    { key: 'activeProjects', label: 'Active Projects', color: 'green-600', bgColor: 'bg-green-100', icon: 'document' },
    { key: 'teamMembers', label: 'Team Members', color: 'blue-600', bgColor: 'bg-blue-100', icon: 'team' },
    { key: 'conversionRate', label: 'Conversion Rate', color: 'purple-600', bgColor: 'bg-purple-100', icon: 'chart', suffix: '%' }
  ],
  director: [
    { key: 'totalLeads', label: 'Total Leads', color: 'tenq-gold', bgColor: 'bg-tenq-gold/10', icon: 'users' },
    { key: 'activeProjects', label: 'Active Projects', color: 'green-600', bgColor: 'bg-green-100', icon: 'document' },
    { key: 'teamMembers', label: 'Team Members', color: 'blue-600', bgColor: 'bg-blue-100', icon: 'team' },
    { key: 'conversionRate', label: 'Conversion Rate', color: 'purple-600', bgColor: 'bg-purple-100', icon: 'chart', suffix: '%' }
  ],
  manager: [
    { key: 'totalLeads', label: 'Total Leads', color: 'tenq-gold', bgColor: 'bg-tenq-gold/10', icon: 'users' },
    { key: 'activeProjects', label: 'Active Projects', color: 'green-600', bgColor: 'bg-green-100', icon: 'document' },
    { key: 'teamMembers', label: 'Team Members', color: 'blue-600', bgColor: 'bg-blue-100', icon: 'team' },
    { key: 'conversionRate', label: 'Conversion Rate', color: 'purple-600', bgColor: 'bg-purple-100', icon: 'chart', suffix: '%' }
  ],
  leader: [
    { key: 'totalLeads', label: 'Total Leads', color: 'tenq-gold', bgColor: 'bg-tenq-gold/10', icon: 'users' },
    { key: 'activeProjects', label: 'Active Projects', color: 'green-600', bgColor: 'bg-green-100', icon: 'document' },
    { key: 'teamMembers', label: 'Team Members', color: 'blue-600', bgColor: 'bg-blue-100', icon: 'team' },
    { key: 'conversionRate', label: 'Conversion Rate', color: 'purple-600', bgColor: 'bg-purple-100', icon: 'chart', suffix: '%' }
  ],
  agent: [
    { key: 'total', label: 'Total Leads', color: 'tenq-gold', bgColor: 'bg-tenq-gold/10', icon: 'users' },
    { key: 'new', label: 'New Leads', color: 'blue-600', bgColor: 'bg-blue-100', icon: 'plus' },
    { key: 'inProgress', label: 'In Progress', color: 'orange-600', bgColor: 'bg-orange-100', icon: 'lightning' },
    { key: 'appointments', label: 'Appointments', color: 'green-600', bgColor: 'bg-green-100', icon: 'calendar' }
  ],
  telemarketer: [
    { key: 'total', label: 'Total Leads', color: 'tenq-gold', bgColor: 'bg-tenq-gold/10', icon: 'users' },
    { key: 'new', label: 'New Leads', color: 'blue-600', bgColor: 'bg-blue-100', icon: 'plus' },
    { key: 'inProgress', label: 'In Progress', color: 'orange-600', bgColor: 'bg-orange-100', icon: 'lightning' },
    { key: 'appointments', label: 'Appointments', color: 'green-600', bgColor: 'bg-green-100', icon: 'calendar' }
  ],
  sid: [
    { key: 'totalProjects', label: 'Total Projects', color: 'blue-600', bgColor: 'bg-blue-100', icon: 'folder' },
    { key: 'activeProjects', label: 'Active Projects', color: 'green-600', bgColor: 'bg-green-100', icon: 'lightning' },
    { key: 'pendingApproval', label: 'Pending Approval', color: 'yellow-600', bgColor: 'bg-yellow-100', icon: 'clock' },
    { key: 'totalValue', label: 'Total Value', color: 'purple-600', bgColor: 'bg-purple-100', icon: 'currency', format: 'currency' }
  ],
  asid: [
    { key: 'totalProjects', label: 'Total Projects', color: 'blue-600', bgColor: 'bg-blue-100', icon: 'folder' },
    { key: 'activeProjects', label: 'Active Projects', color: 'green-600', bgColor: 'bg-green-100', icon: 'lightning' },
    { key: 'pendingApproval', label: 'Pending Approval', color: 'yellow-600', bgColor: 'bg-yellow-100', icon: 'clock' },
    { key: 'totalValue', label: 'Total Value', color: 'purple-600', bgColor: 'bg-purple-100', icon: 'currency', format: 'currency' }
  ],
  designer: [
    { key: 'totalProjects', label: 'Total Projects', color: 'blue-600', bgColor: 'bg-blue-100', icon: 'folder' },
    { key: 'activeProjects', label: 'Active Projects', color: 'green-600', bgColor: 'bg-green-100', icon: 'lightning' },
    { key: 'pendingApproval', label: 'Pending Approval', color: 'yellow-600', bgColor: 'bg-yellow-100', icon: 'clock' },
    { key: 'completedProjects', label: 'Completed', color: 'purple-600', bgColor: 'bg-purple-100', icon: 'check' }
  ],
  draughter_2d: [
    { key: 'totalProjects', label: 'Total Projects', color: 'blue-600', bgColor: 'bg-blue-100', icon: 'folder' },
    { key: 'activeProjects', label: 'Active Projects', color: 'green-600', bgColor: 'bg-green-100', icon: 'lightning' },
    { key: 'pendingApproval', label: 'Pending Approval', color: 'yellow-600', bgColor: 'bg-yellow-100', icon: 'clock' },
    { key: 'completedProjects', label: 'Completed', color: 'purple-600', bgColor: 'bg-purple-100', icon: 'check' }
  ],
  quotation_team: [
    { key: 'totalProjects', label: 'Total Projects', color: 'blue-600', bgColor: 'bg-blue-100', icon: 'folder' },
    { key: 'activeProjects', label: 'Active Projects', color: 'green-600', bgColor: 'bg-green-100', icon: 'lightning' },
    { key: 'pendingApproval', label: 'Pending Approval', color: 'yellow-600', bgColor: 'bg-yellow-100', icon: 'clock' },
    { key: 'completedProjects', label: 'Completed', color: 'purple-600', bgColor: 'bg-purple-100', icon: 'check' }
  ],
  draughter_site: [
    { key: 'myAssigned', label: 'Assigned to Me', color: 'blue-600', bgColor: 'bg-blue-100', icon: 'user' },
    { key: 'myInProgress', label: 'In Progress', color: 'purple-600', bgColor: 'bg-purple-100', icon: 'lightning' },
    { key: 'myCompleted', label: 'Completed', color: 'green-600', bgColor: 'bg-green-100', icon: 'check' }
  ],
  legal_team: [
    { key: 'totalProjects', label: 'Total Projects', color: 'blue-600', bgColor: 'bg-blue-100', icon: 'folder' },
    { key: 'activeProjects', label: 'Active Projects', color: 'green-600', bgColor: 'bg-green-100', icon: 'lightning' },
    { key: 'pendingContracts', label: 'Pending Contracts', color: 'yellow-600', bgColor: 'bg-yellow-100', icon: 'clock' },
    { key: 'completedContracts', label: 'Completed', color: 'purple-600', bgColor: 'bg-purple-100', icon: 'check' }
  ]
};

// Quick actions configuration per role
export const QUICK_ACTIONS_CONFIG = {
  system_admin: [
    { label: 'View All Projects', path: '/projects', variant: 'primary' },
    { label: 'Manage Leads', path: '/leads', variant: 'secondary' },
    { label: 'Manage Users', path: '/users', variant: 'outline' },
    { label: 'Distribute Leads', path: '/distribution', variant: 'outline-gold' }
  ],
  admin: [
    { label: 'View All Projects', path: '/projects', variant: 'primary' },
    { label: 'Manage Leads', path: '/leads', variant: 'secondary' },
    { label: 'Manage Users', path: '/users', variant: 'outline' },
    { label: 'Distribute Leads', path: '/distribution', variant: 'outline-gold' }
  ],
  director: [
    { label: 'View All Projects', path: '/projects', variant: 'primary' },
    { label: 'Manage Leads', path: '/leads', variant: 'secondary' },
    { label: 'Manage Users', path: '/users', variant: 'outline' },
    { label: 'Distribute Leads', path: '/distribution', variant: 'outline-gold' }
  ],
  manager: [
    { label: 'View All Projects', path: '/projects', variant: 'primary' },
    { label: 'Manage Leads', path: '/leads', variant: 'secondary' },
    { label: 'Manage Users', path: '/users', variant: 'outline' },
    { label: 'Attendance Map', path: '/dashboard?tab=site-attendance-map', variant: 'outline-gold' }
  ],
  leader: [
    { label: 'View All Projects', path: '/projects', variant: 'primary' },
    { label: 'Manage Leads', path: '/leads', variant: 'secondary' },
    { label: 'Distribute Leads', path: '/distribution', variant: 'outline-gold' }
  ],
  agent: [
    { label: 'View My Leads', path: '/my-leads', variant: 'primary' },
    { label: 'New Leads to Contact', path: '/my-leads?filter=new', variant: 'secondary' },
    { label: 'Create Sales Form', path: '/my-leads?status=pending_booking', variant: 'success', icon: 'document' }
  ],
  telemarketer: [
    { label: 'View My Leads', path: '/my-leads', variant: 'primary' },
    { label: 'New Leads to Contact', path: '/my-leads?filter=new', variant: 'secondary' }
  ],
  sid: [
    { label: 'View All Projects', path: '/projects', variant: 'primary' },
    { label: 'My DD Tasks', path: '/dashboard?tab=my-dd-tasks', variant: 'secondary' },
    { label: 'My 2D Tasks', path: '/dashboard?tab=my-2d-tasks', variant: 'outline' },
    { label: 'My 3D Tasks', path: '/dashboard?tab=my-3d-tasks', variant: 'outline' }
  ],
  asid: [
    { label: 'View All Projects', path: '/projects', variant: 'primary' },
    { label: 'My DD Tasks', path: '/dashboard?tab=my-dd-tasks', variant: 'secondary' },
    { label: 'My 2D Tasks', path: '/dashboard?tab=my-2d-tasks', variant: 'outline' },
    { label: 'My 3D Tasks', path: '/dashboard?tab=my-3d-tasks', variant: 'outline' }
  ],
  designer: [
    { label: 'View My 3D Tasks', path: '/dashboard?tab=my-3d-tasks', variant: 'primary' },
    { label: 'View My DD Tasks', path: '/dashboard?tab=my-dd-tasks', variant: 'secondary' },
    { label: 'View Projects', path: '/projects', variant: 'outline' }
  ],
  draughter_2d: [
    { label: 'View My 2D Tasks', path: '/dashboard?tab=my-2d-tasks', variant: 'primary' },
    { label: 'View My DD Tasks', path: '/dashboard?tab=my-dd-tasks', variant: 'secondary' },
    { label: 'View Projects', path: '/projects', variant: 'outline' }
  ],
  quotation_team: [
    { label: 'My Quotation Tasks', path: '/dashboard?tab=my-quotation-tasks', variant: 'primary' },
    { label: 'View Projects', path: '/projects', variant: 'secondary' }
  ],
  draughter_site: [],
  legal_team: [
    { label: 'View Projects', path: '/projects', variant: 'primary' }
  ]
};

/**
 * Get tabs for a specific role
 * @param {string} role - User role
 * @returns {Array} Array of tab objects
 */
export function getTabsForRole(role) {
  const tabIds = ROLE_TABS[role] || [];
  return tabIds.map(id => TABS[id]).filter(Boolean);
}

/**
 * Get role subtitle for dashboard header
 * @param {string} role - User role
 * @returns {string} Subtitle text
 */
export function getRoleSubtitle(role) {
  return ROLE_SUBTITLES[role] || 'Dashboard';
}

/**
 * Get stats configuration for a role
 * @param {string} role - User role
 * @returns {Array} Array of stats config objects
 */
export function getStatsConfig(role) {
  return STATS_CONFIG[role] || [];
}

/**
 * Get quick actions for a role
 * @param {string} role - User role
 * @returns {Array} Array of quick action configs
 */
export function getQuickActions(role) {
  return QUICK_ACTIONS_CONFIG[role] || [];
}

/**
 * Check if role belongs to a category
 * @param {string} role - User role
 * @param {string} category - Category name
 * @returns {boolean}
 */
export function isRoleInCategory(role, category) {
  return ROLE_CATEGORIES[category]?.includes(role) || false;
}

/**
 * Check if role has access to a specific tab
 * @param {string} role - User role
 * @param {string} tabId - Tab ID
 * @returns {boolean}
 */
export function hasTabAccess(role, tabId) {
  return ROLE_TABS[role]?.includes(tabId) || false;
}
